import {createReducer} from "redux-act";
import * as actions from "modules/actions";
import {RequestStateType} from "modules/utils";
import {IError} from "modules/types";

export interface IUser {
	id: number;
	first_name: string;
	last_name: string;
	email: string;
	birthday: string;
	country_short_name: string;
	country: string;
	recovered: boolean;
	dornaId?: string;
	is_predictor: 0 | 1;
	recoverd: number;
	username?: string;
}

export interface IUserUpdate {
	username?: string;
	isNotificationsEnabled?: boolean;
	password?: string;
	oldPassword?: string;
}

export interface IUserReducer {
	is_authenticated: boolean;
	is_data_requested?: boolean;
	is_additional_data_required: boolean;
	is_return_data_required: boolean;
	is_need_to_register_for_game: boolean;
	is_saved: boolean;
	user: IUser | Record<string, unknown>;
	error?: IError;
	sso_data?: actions.IUserLoginPayload;
	user_updated: boolean;
	is_session_checked: boolean;
	forgot_password_request: RequestStateType;
	restore_password_request: RequestStateType;
	registration_request: RequestStateType;
	login_request: RequestStateType;
}

const defaultState = {
	is_authenticated: false,
	user: {},
	is_additional_data_required: false,
	is_return_data_required: false,
	is_need_to_register_for_game: false,
	is_saved: false,
	user_updated: false,
	is_session_checked: false,
	forgot_password_request: RequestStateType.IDLE,
	restore_password_request: RequestStateType.IDLE,
	registration_request: RequestStateType.IDLE,
	login_request: RequestStateType.IDLE,
};

const onUserReceived = (state: IUserReducer, payload: IUser) => ({
	...state,
	is_authenticated: true,
	is_session_checked: true,
	is_data_requested: false,
	is_additional_data_required: false,
	is_return_data_required: payload.recovered === false,
	is_need_to_register_for_game: payload.is_predictor === 0,
	registration_request: RequestStateType.Received,
	login_request: RequestStateType.Received,
	user: payload,
});

const onUserUpdated = (state: IUserReducer, payload: IUser) => ({
	...state,
	user: payload,
	user_updated: true,
});

const onResetUserUpdated = (state: IUserReducer) => ({
	...state,
	user_updated: false,
});

export const user = createReducer<IUserReducer>({}, defaultState)
	.on(actions.userLogin, (state, sso_data) => ({
		...state,
		is_data_requested: true,
		is_additional_data_required: false,
		is_return_data_required: false,
	}))
	.on(actions.userLoginSuccess, onUserReceived)
	.on(actions.userFetchSuccess, onUserReceived)
	.on(actions.userFetchFailed, (state) => ({
		...state,
		is_session_checked: true,
	}))
	.on(actions.userCreateSuccess, onUserReceived)
	.on(actions.backdoorLoginSuccess, onUserReceived)
	.on(actions.userLogout, (state) => ({...state, ...defaultState}))
	.on(actions.userLogoutSuccess, () => ({...defaultState}))
	.on(actions.userAskForRegistration, (state, payload) => {
		return {
			...state,
			user: {
				...state.user,
				...payload,
			},
			is_data_requested: false,
			is_additional_data_required: true,
		};
	})
	.on(actions.userAskForReturn, (state) => ({
		...state,
		is_data_requested: false,
		is_return_data_required: true,
	}))
	.on(actions.userReturnSuccess, onUserReceived)
	.on(actions.userRegisterForGameSuccess, onUserReceived)
	.on(actions.userUpdateSuccess, onUserUpdated)
	.on(actions.resetUserUpdateSuccess, onResetUserUpdated)
	.on(actions.postUserLogoutSuccess, (state) => ({
		...state,
		is_authenticated: false,
	}))
	.on(actions.postForgotPassword, (state) => ({
		...state,
		forgot_password_request: RequestStateType.Requested,
	}))
	.on(actions.postForgotPasswordSuccess, (state) => ({
		...state,
		forgot_password_request: RequestStateType.Received,
	}))
	.on(actions.postForgotPasswordFailed, (state) => ({
		...state,
		forgot_password_request: RequestStateType.Error,
	}))
	.on(actions.postRestorePassword, (state) => ({
		...state,
		restore_password_request: RequestStateType.Requested,
	}))
	.on(actions.postRestorePasswordSuccess, (state) => ({
		...state,
		restore_password_request: RequestStateType.Received,
	}))
	.on(actions.postRestorePasswordFailed, (state, payload) => ({
		...state,
		restore_password_request: RequestStateType.Error,
		error: payload,
	}))
	.on(actions.postUserCreate, (state) => ({
		...state,
		registration_request: RequestStateType.Requested,
	}))
	.on(actions.postUserCreateFailed, (state, payload) => ({
		...state,
		registration_request: RequestStateType.Error,
		error: payload,
	}))
	.on(actions.postLogin, (state) => ({
		...state,
		login_request: RequestStateType.Requested,
	}))
	.on(actions.postLoginClear, (state) => ({
		...state,
		error: undefined
	}))
	.on(actions.resetUserStore, (state) => ({
		...state,
		login_request: RequestStateType.IDLE,
		registration_request: RequestStateType.IDLE,
		restore_password_request: RequestStateType.IDLE,
		error: undefined,
	}));
